/**
 * The single source of musical time.
 *
 * Everything that has to line up with the song — wave spawning, gate judging,
 * the mandala's pulse — reads this and nothing else. Not `performance.now()`,
 * not a frame delta accumulator, not `Date.now()`. The Unity build already made
 * this choice correctly by scheduling against `AudioSettings.dspTime`, and the
 * Web Audio equivalent is `AudioContext.currentTime`.
 *
 * The one thing Unity's `dspTime` hides that this must handle explicitly is
 * **output latency**. `currentTime` advances on the audio graph's own timeline,
 * but the sample it describes has not reached the speaker yet: it is still
 * travelling through the render quantum and the OS buffer. Subtracting
 * `baseLatency + outputLatency` converts "time in the graph" into "time the
 * listener is hearing", which is the frame of reference gameplay has to use.
 * Skipping it puts every judgement tens of milliseconds early.
 */

/**
 * The slice of `AudioContext` this needs. Kept narrow so tests can fake it.
 *
 * The latency fields are `| undefined` and not merely optional: under
 * `exactOptionalPropertyTypes` those mean different things, and browsers really
 * do hand back an implemented property whose value is `undefined`.
 */
export interface AudioTimeSource {
  readonly currentTime: number;
  readonly baseLatency?: number | undefined;
  readonly outputLatency?: number | undefined;
}

export type MusicClockState = 'idle' | 'scheduled' | 'running';

export class MusicClock {
  private startTime = 0;
  private scheduled = false;

  constructor(private readonly source: AudioTimeSource) {}

  /**
   * Seconds between the graph's clock and the listener's ear.
   *
   * `outputLatency` is unimplemented in some browsers, and `baseLatency` in
   * others, so both are read defensively; a missing value is zero rather than
   * NaN, which would poison every downstream comparison.
   */
  get outputLatency(): number {
    const base = numberOrZero(this.source.baseLatency);
    const output = numberOrZero(this.source.outputLatency);
    return base + output;
  }

  /** Context time the song was scheduled to begin at. */
  get scheduledStart(): number {
    return this.startTime;
  }

  get state(): MusicClockState {
    if (!this.scheduled) return 'idle';
    return this.source.currentTime >= this.startTime ? 'running' : 'scheduled';
  }

  /**
   * Anchors the clock to a context timestamp.
   *
   * Called with the same value handed to `AudioBufferSourceNode.start`, so the
   * clock and the audio share one origin rather than being started "at about the
   * same time" from two places.
   */
  startAt(contextTime: number): void {
    this.startTime = contextTime;
    this.scheduled = true;
  }

  reset(): void {
    this.startTime = 0;
    this.scheduled = false;
  }

  /**
   * Seconds into the song as the listener hears it.
   *
   * Negative before the scheduled start, which is deliberate: it lets a count-in
   * read the approach to zero without a separate mechanism.
   */
  now(): number {
    if (!this.scheduled) return 0;
    return this.source.currentTime - this.startTime - this.outputLatency;
  }

  /** `now()` floored at zero, for callers that only care once the song is under way. */
  elapsed(): number {
    return Math.max(0, this.now());
  }
}

function numberOrZero(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
